import { ConnectionStateEnum } from '@core/model/domain/bankConnection';
import { Result } from '@core/model/framework/Result/Result';
import { GetAccounts } from '@core/usecase/getAccounts';
import { WaitOnWebauthNotification } from '@core/usecase/waitOnWebauthNotification';
import { ProcessEventFactory } from '@shared/models/processEvents';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { delay, from } from 'rxjs';

export class WaitOnExternalBankLoginVM {
  constructor(
    private processEventService: ProcessEventService,
    private storeService: StoreService,
    private waitOnWebauthNotification: WaitOnWebauthNotification,
    private getAccounts: GetAccounts
  ) {}

  async onInit() {
    if (this.storeService.getVariableOrThrow('IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER') === false) {
      await this.waitOnNewBankConnectionNotification();
    }
  }

  async waitOnNewBankConnectionNotification() {
    try {
      const result = await this.waitOnWebauthNotification.execute({
        userId: this.storeService.getVariableOrThrow('USER_ID'),
        userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
      });

      if (result.success === false) {
        return Result.matchBadWithDefault(result, {
          STOPPED_POLLING_VIA_USER_ACTION: () => {},
          default: () => this.processEventService.push(ProcessEventFactory.SpawnAskToRetryWebauth()),
        });
      }

      const newBankConnection = result.value;

      if (newBankConnection.state === ConnectionStateEnum.VALID) {
        const userId = this.storeService.getVariableOrThrow('USER_ID');
        const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');

        /* 
          Delay of 1s between last /connection and /accounts to be sure bank account is
          stored in DB
        */
        from(
          this.getAccounts.execute({
            connectionId: newBankConnection.connectionId,
            userId,
            userToken,
          })
        )
          .pipe(delay(1000))
          .subscribe({
            next: (result) => {
              const accounts = Result.unwrap(result);
              this.processEventService.push(ProcessEventFactory.SpawnAccountsFoundEvent(accounts));
              this.storeService.setVariable('SELECTED_CONNECTION_ID', newBankConnection.connectionId);
            },
            error: (error) => {
              this.processEventService.push(
                ProcessEventFactory.SpawnInternalErrorEvent({
                  errorOrigin: 'WaitOnExternalBankLoginVM - waitOnNewBankConnectionNotification - catch',
                  error,
                })
              );
            },
          });
      } else {
        this.processEventService.push(ProcessEventFactory.SpawnAskToRetryWebauth());
      }
    } catch (error) {
      this.processEventService.push(
        ProcessEventFactory.SpawnInternalErrorEvent({
          errorOrigin: 'WaitOnExternalBankLoginVM - waitOnNewBankConnectionNotification - catch',
          error,
        })
      );
    }
  }

  stopPolling() {
    if (this.storeService.getVariableOrThrow('IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER') === false) {
      this.waitOnWebauthNotification.stop();
    }
  }

  backToBankChoice() {
    this.processEventService.push(ProcessEventFactory.SpawnGoBackToBankChoiceEvent());
  }
}
